import { useEffect, useRef, useState } from 'react';
import axios from 'config/axios';
import { DEFAULT_CYCLE_YEAR } from 'global/fillapp';

// Directory row: [name, city, state]. Arrays rather than objects — the schools
// file is ~37k rows and this halves its size.
export type DirectoryRow = [string, string, string];

interface DirectoryPayload {
    cycle: number;
    count: number;
    schools?: DirectoryRow[];
    colleges?: DirectoryRow[];
}

// Bump when the payload shape changes so stale caches are dropped.
const CACHE_PREFIX = 'fillapp.directory.v1.';

// Module-level cache: the schools directory is ~1.7 MB of JSON, and several
// SchoolLookup fields render on one subtab — parse it once per page, not per field.
const memo: Record<string, DirectoryRow[] | undefined> = {};
const inflight: Record<string, Promise<DirectoryRow[]> | undefined> = {};

function fetchDirectory(kind: 'schools' | 'colleges-all', cycleYear: number): Promise<DirectoryRow[]> {
    const key = `${kind}.${cycleYear}`;
    if (memo[key]) return Promise.resolve(memo[key]!);
    if (inflight[key]) return inflight[key]!;

    try {
        const cached = localStorage.getItem(CACHE_PREFIX + key);
        if (cached) {
            const rows = JSON.parse(cached) as DirectoryRow[];
            memo[key] = rows;
            return Promise.resolve(rows);
        }
    } catch { /* corrupt cache — refetch */ }

    inflight[key] = axios.get(`fillapp/${kind}/${cycleYear}/`)
        .then(res => {
            const data = res.data as DirectoryPayload;
            const rows = (data.schools || data.colleges || []) as DirectoryRow[];
            memo[key] = rows;
            try {
                localStorage.setItem(CACHE_PREFIX + key, JSON.stringify(rows));
            } catch { /* localStorage full — memory cache still works */ }
            return rows;
        })
        .finally(() => { delete inflight[key]; });
    return inflight[key]!;
}

// Lazy directory for the lookup type-aheads. `enabled` gates the fetch so the
// 1.7 MB schools file downloads on first field focus, not on subtab render.
export function useFillAppDirectory(
    kind: 'schools' | 'colleges-all',
    enabled: boolean,
    cycleYear: number = DEFAULT_CYCLE_YEAR,
) {
    const [rows, setRows] = useState<DirectoryRow[] | null>(memo[`${kind}.${cycleYear}`] ?? null);
    const [error, setError] = useState<string | null>(null);
    const wanted = useRef(false);

    useEffect(() => {
        if (!enabled || error || wanted.current) return;
        wanted.current = true;
        let cancelled = false;
        fetchDirectory(kind, cycleYear)
            .then(r => { if (!cancelled) setRows(r); })
            .catch(() => {
                // Clear the guard so retry() can trigger a refetch — a transient
                // failure must not disable the lookup until a full page reload.
                wanted.current = false;
                if (!cancelled) setError('directory unavailable');
            });
        return () => { cancelled = true; };
    }, [enabled, error, kind, cycleYear]);

    // Clearing the error re-arms the effect above (error is a dependency).
    const retry = () => setError(null);

    return { rows, error, retry };
}
